import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '#app/utils/misc.tsx'

const buttonVariants = cva(
	[
		'inline-flex items-center justify-center gap-2.5',
		'whitespace-nowrap rounded-none border border-transparent',
		'font-sans text-sm font-medium uppercase tracking-[0.04em]',
		'ring-offset-background',
		'transition-[color,background-color,border-color,transform] duration-200',
		'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2',
		'disabled:pointer-events-none disabled:opacity-50',
		'cursor-pointer',
		'[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
		'[&_.arrow]:transition-transform [&_.arrow]:duration-200',
		'hover:[&_.arrow]:translate-x-1',
	].join(' '),
	{
		variants: {
			variant: {
				// Oxford filled in light mode, gold filled in dark mode (via inverted --primary token).
				// On hover the fill drops to reveal the brand color text on the warm paper bg.
				default:
					'bg-primary text-primary-foreground border-primary hover:bg-transparent hover:text-primary',
				// Architectural Gold filled in both modes.
				gold: 'bg-accent text-accent-foreground border-accent hover:bg-transparent hover:text-accent',
				// Subtle outlined button — border picks up gold on hover.
				outline:
					'border-border bg-transparent text-foreground hover:border-gold hover:text-gold',
				// Neutral filled.
				secondary:
					'bg-secondary text-secondary-foreground border-secondary hover:bg-secondary/80',
				// Text-only with gold hover.
				ghost: 'text-foreground hover:text-gold',
				// Inline link with a gold underline rule — used for secondary CTAs.
				link: 'h-auto px-0 py-0 pb-1 border-0 border-b border-gold rounded-none gap-2 text-foreground tracking-[0.06em] hover:text-gold',
				destructive:
					'bg-destructive text-destructive-foreground border-destructive hover:bg-destructive/90',
			},
			size: {
				default: 'h-12 px-7',
				sm: 'h-10 px-5 text-xs',
				lg: 'h-14 px-8',
				icon: 'h-12 w-12',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button'
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		)
	},
)
Button.displayName = 'Button'

export { Button, buttonVariants }
